"use client"

import { useState, useEffect, useRef } from "react"
import type { HeroInventory, ItemDefinition, InventoryItem } from "@/types/item.types"
import { getItemDefinition, canUseItem } from "@/systems/item-system"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"

interface InventorySectionProps {
  scale: number
  inventory?: HeroInventory
  onUseItem?: (slotIndex: number) => void
  pendingItemSlot?: number | null
  onDragStart?: (slotIndex: number, itemDefId: string) => void
  onDragEnd?: () => void
  disableClicks?: boolean // Disable click handlers (for mobile - clicks handled by bottom sheet)
  showBeaconParticles?: boolean // Show blue particles on beacon when clone purchased
  variant?: "game" | "shop"
  shopRate?: number // Shop sell rate (0.35, 0.5, or 0.65) - only for shop variant
  onSellItem?: (slotIndex: number) => void // Callback when selling an item - only for shop variant
}

// Component to render item icon from individual image file
const ItemSpriteIcon = ({
  itemDef,
  size,
  showOutline = false,
}: {
  itemDef: ItemDefinition
  size: number
  showOutline?: boolean
}) => {
  // Use the new image path if available
  if (itemDef.image) {
    return (
      <img
        src={itemDef.image}
        alt={itemDef.name}
        width={size}
        height={size}
        style={{
          imageRendering: "pixelated",
          filter: showOutline ? "drop-shadow(0 0 2px white)" : undefined
        }}
        className="object-contain"
      />
    )
  }

  // Fallback to colored placeholder
  return (
    <div
      className="rounded"
      style={{
        width: size,
        height: size,
        backgroundColor: itemDef.iconColor,
      }}
    />
  )
}

const rarityColors: Record<string, string> = {
  common: "text-gray-300",
  rare: "text-blue-400",
  epic: "text-purple-400",
  legendary: "text-amber-400",
  unique: "text-orange-400",
}

const effectTypeLabels: Record<string, string> = {
  heal: "Heal",
  mana: "Mana",
  "damage-boost": "Damage",
  "speed-boost": "Speed",
  teleport: "Teleport",
  lure: "Lure",
  absorb: "Shield",
  armor: "Armor",
  lifesteal: "Lifesteal",
  "spell-power": "Spell Power",
  "max-health": "Max Health",
  "crit-chance": "Crit Chance",
  "attack-speed": "Attack Speed",
  "cooldown-reduction": "Cooldown",
  "gold-bonus": "Gold Bonus",
  "damage-reflect": "Reflect",
  "mana-regen": "Mana Regen",
  "hp-regen": "HP Regen",
}

const ItemTooltip = ({ itemDef, slot, variant = "game" }: { itemDef: ItemDefinition; slot: InventoryItem; variant?: "game" | "shop" }) => {
  const hasCooldown = itemDef.cooldown && itemDef.cooldown > 0
  const isOnCooldown = slot.currentCooldown > 0
  const isBeacon = slot.itemDefId === "clone-activation-beacon"

  return (
    <div className="w-[140px] p-1.5">
      {/* Header */}
      <div className="flex items-center justify-between mb-0.5">
        <span className={`font-bold text-[11px] ${isBeacon ? "text-blue-400" : rarityColors[itemDef.rarity]}`}>{itemDef.name}</span>
        {!isBeacon && slot.quantity > 1 && (
          <span className="text-[9px] text-gray-400">x{slot.quantity}</span>
        )}
      </div>

      {/* Type & Rarity */}
      <div className="flex items-center gap-1 mb-1">
        <span className="text-[7px] text-gray-500 capitalize">{itemDef.rarity}</span>
        <span className="text-[7px] text-gray-600">•</span>
        <span className="text-[7px] text-gray-500 capitalize">{itemDef.type}</span>
      </div>

      {/* Description */}
      <p className="text-[9px] text-gray-300 leading-tight mb-1">{itemDef.description}</p>

      {/* Beacon-specific info */}
      {isBeacon && (
        <div className="border-t border-blue-600/30 pt-1 space-y-0.5 text-[9px]">
          <div className="flex justify-between">
            <span className="text-gray-500">Stored Clones</span>
            <span className="text-blue-400">{slot.charges || 0}</span>
          </div>
          <p className="text-[8px] text-gray-500 mt-1">
            Purchase clones via the droppod menu to increase charges
          </p>
        </div>
      )}

      {/* Effects */}
      {!isBeacon && itemDef.effects.length > 0 && (
        <div className="border-t border-amber-600/30 pt-1 space-y-0.5 text-[9px]">
          {itemDef.effects.map((effect, idx) => (
            <div key={idx} className="flex justify-between">
              <span className="text-gray-500">{effectTypeLabels[effect.type] || effect.type}</span>
              <span className={effect.value > 0 ? "text-green-400" : "text-red-400"}>
                {effect.type === "lifesteal" || effect.type === "spell-power" || effect.type === "crit-chance" || effect.type === "attack-speed" || effect.type === "cooldown-reduction" || effect.type === "gold-bonus" || effect.type === "damage-reflect"
                  ? `${(effect.value * 100).toFixed(0)}%`
                  : effect.value > 0
                    ? `+${effect.value}`
                    : effect.value}
                {effect.duration && ` (${(effect.duration / 60).toFixed(1)}s)`}
              </span>
            </div>
          ))}
          {/* Bonus effects for legendary items - shown in green */}
          {itemDef.bonusEffects && itemDef.bonusEffects.map((effect, idx) => (
            <div key={`bonus-${idx}`} className="flex justify-between">
              <span className="text-emerald-500 font-medium">{effectTypeLabels[effect.type] || effect.type}</span>
              <span className="text-emerald-400 font-medium">
                {effect.type === "lifesteal" || effect.type === "spell-power" || effect.type === "crit-chance" || effect.type === "attack-speed" || effect.type === "cooldown-reduction" || effect.type === "gold-bonus" || effect.type === "damage-reflect"
                  ? `+${(effect.value * 100).toFixed(0)}%`
                  : `+${effect.value}`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Cooldown - only show for game variant */}
      {variant === "game" && hasCooldown && (
        <div className="mt-1 pt-1 border-t border-amber-600/30 flex justify-between items-center text-[9px]">
          <span className="text-gray-500">Cooldown</span>
          <span className={isOnCooldown ? "text-red-400" : "text-yellow-400"}>
            {isOnCooldown
              ? `${Math.ceil(slot.currentCooldown / 60)}s remaining`
              : `${(itemDef.cooldown! / 60).toFixed(1)}s`}
          </span>
        </div>
      )}

      {/* Cost - don't show for beacon (cannot be sold) */}
      {!isBeacon && (
        <div className="mt-1 pt-1 border-t border-amber-600/30 flex justify-between items-center text-[9px]">
          <span className="text-gray-500">Value</span>
          <span className="text-amber-400">{itemDef.cost} gold</span>
        </div>
      )}

      {/* Usage hint - don't show for beacon or shop variant */}
      {variant === "game" && !isBeacon && (
        <div className="mt-1 text-[8px] text-gray-500">
          {itemDef.type === "consumable" ? "Click to use" : "Click to equip"}
        </div>
      )}
    </div>
  )
}

// Particle interface for beacon effect
interface BeaconParticle {
  id: number
  x: number
  y: number
  vx: number
  vy: number
  size: number
  opacity: number
  life: number
}

export const InventorySection = ({ scale, inventory, onUseItem, pendingItemSlot, onDragStart, onDragEnd, disableClicks = false, showBeaconParticles = false, variant = "game", shopRate, onSellItem }: InventorySectionProps) => {
  const slotSize = 22 * scale
  const gap = 5 * scale
  const left = variant === "game" ? 333 * scale : 328 * scale
  const top = 80 * scale
  const sectionId = variant === "game" ? "inventory-section" : "shop-inventory-section"

  // Track which slot is flashing (showing white outline) - game variant only
  const [flashingSlot, setFlashingSlot] = useState<number | null>(null)

  // Track which slot is showing the sell confirmation bubble - shop variant only
  const [sellBubbleSlot, setSellBubbleSlot] = useState<number | null>(null)

  // Beacon particle effect state - game variant only
  const [beaconParticles, setBeaconParticles] = useState<BeaconParticle[]>([])
  const particleIdRef = useRef(0)

  // Clear flash after brief delay - game variant only
  useEffect(() => {
    if (variant === "shop") return
    if (flashingSlot !== null) {
      const timer = setTimeout(() => setFlashingSlot(null), 150)
      return () => clearTimeout(timer)
    }
  }, [flashingSlot, variant])

  // Spawn beacon particles when showBeaconParticles becomes true - game variant only
  useEffect(() => {
    if (variant === "shop" || !showBeaconParticles) return

    const newParticles: BeaconParticle[] = []
    const particleCount = 20

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2
      const speed = 1 + Math.random() * 2
      newParticles.push({
        id: particleIdRef.current++,
        x: slotSize / 2,
        y: slotSize / 2,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 2 + Math.random() * 3,
        opacity: 1,
        life: 1,
      })
    }
    setBeaconParticles(prev => [...prev, ...newParticles])
  }, [showBeaconParticles, slotSize, variant])

  // Animate beacon particles - game variant only
  useEffect(() => {
    if (variant === "shop" || beaconParticles.length === 0) return

    const interval = setInterval(() => {
      setBeaconParticles(prev =>
        prev
          .map(p => ({
            ...p,
            x: p.x + p.vx,
            y: p.y + p.vy,
            life: p.life - 0.03,
            opacity: p.life,
          }))
          .filter(p => p.life > 0)
      )
    }, 16)

    return () => clearInterval(interval)
  }, [beaconParticles.length, variant])

  return (
    <TooltipProvider delayDuration={100}>
      <div
        id={sectionId}
        className="absolute grid grid-cols-3"
        style={{
          left,
          top,
          gap,
        }}
      >
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const slot = inventory?.slots[i]
          const itemDef = slot ? getItemDefinition(slot.itemDefId) : null
          // Check if this is the Clone Activation Codex (special item)
          const isBeacon = slot?.itemDefId === "clone-activation-beacon"
          // Equipment is always clickable, consumables check canUseItem, special items are not usable
          const isEquipment = itemDef?.type === "equipment"
          const isSpecial = itemDef?.type === "special"
          const usable = !isSpecial && (isEquipment || (inventory ? canUseItem(inventory, i) : false))
          const onCooldown = slot && slot.currentCooldown > 0
          const isPending = pendingItemSlot === i
          const isFlashing = flashingSlot === i

          // Empty slot - no tooltip
          if (!slot || !itemDef) {
            const slotId = variant === "game" ? `inventory-slot-${i}` : `shop-inventory-slot-${i}`

            // Shop variant uses div
            if (variant === "shop") {
              return (
                <div
                  key={i}
                  id={slotId}
                  className="relative flex items-center justify-center"
                  style={{
                    width: slotSize,
                    height: slotSize,
                    borderRadius: 2 * scale,
                    backgroundColor: "rgb(31, 41, 55)",
                  }}
                />
              )
            }

            // Game variant uses button
            return (
              <button
                key={i}
                id={slotId}
                className="relative transition-colors flex items-center justify-center hover:bg-white/10"
                style={{
                  width: slotSize,
                  height: slotSize,
                  borderRadius: 2 * scale,
                  backgroundColor: "rgb(31, 41, 55)",
                }}
                disabled
              />
            )
          }

          const slotId = variant === "game" ? `inventory-slot-${i}` : `shop-inventory-slot-${i}`

          // Shop variant - clickable for selling (except beacon)
          if (variant === "shop") {
            const sellPrice = isBeacon ? 0 : Math.floor(itemDef.cost * (shopRate || 0.5))
            const canSell = !isBeacon && onSellItem

            return (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <button
                    id={slotId}
                    className={`relative flex items-center justify-center ${canSell ? 'cursor-pointer hover:brightness-110' : 'cursor-default'}`}
                    onClick={() => {
                      if (canSell) {
                        setSellBubbleSlot(sellBubbleSlot === i ? null : i)
                      }
                    }}
                    style={{
                      width: slotSize,
                      height: slotSize,
                      borderRadius: 2 * scale,
                      backgroundColor: itemDef.iconColor,
                      border: isBeacon
                        ? "1px solid #3b82f6"
                        : sellBubbleSlot === i
                          ? "2px solid #fbbf24"
                          : "1px solid #6b7280",
                      boxShadow: isBeacon
                        ? "0 0 6px rgba(59, 130, 246, 0.4)"
                        : sellBubbleSlot === i
                          ? "0 0 8px rgba(251, 191, 36, 0.6)"
                          : undefined,
                    }}
                  >
                    {/* Item icon */}
                    <ItemSpriteIcon
                      itemDef={itemDef}
                      size={Math.floor(slotSize * 0.9)}
                    />

                    {/* Beacon charges badge */}
                    {isBeacon && (
                      <span
                        className="absolute bottom-0 right-0 bg-blue-600/90 text-white font-bold flex items-center justify-center z-10"
                        style={{
                          fontSize: 8 * scale,
                          width: 10 * scale,
                          height: 10 * scale,
                          borderRadius: 2 * scale,
                        }}
                      >
                        {slot.charges || 0}
                      </span>
                    )}

                    {/* Quantity badge (for non-beacon items) */}
                    {!isBeacon && slot.quantity > 1 && (
                      <span
                        className="absolute bottom-0 right-0 bg-black/80 text-white font-bold flex items-center justify-center z-10"
                        style={{
                          fontSize: 8 * scale,
                          width: 10 * scale,
                          height: 10 * scale,
                          borderRadius: 2 * scale,
                        }}
                      >
                        {slot.quantity}
                      </span>
                    )}

                    {/* Sell confirmation bubble */}
                    {sellBubbleSlot === i && canSell && (
                      <div
                        className="absolute bg-amber-950/95 border-2 border-amber-500/80 rounded-md shadow-lg z-50 flex flex-col items-center justify-center px-2 py-1.5"
                        style={{
                          bottom: `${slotSize + 8}px`,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          minWidth: 80 * scale,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="bg-amber-600 hover:bg-amber-500 text-black font-bold rounded px-2 py-0.5 text-xs whitespace-nowrap transition-colors"
                          onClick={(e) => {
                            e.stopPropagation()
                            onSellItem(i)
                            setSellBubbleSlot(null)
                          }}
                        >
                          Sell for ${sellPrice}
                        </button>
                      </div>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-black/95 border border-amber-600/50 shadow-lg shadow-black/50 p-0">
                  <ItemTooltip itemDef={itemDef} slot={slot} variant="shop" />
                </TooltipContent>
              </Tooltip>
            )
          }

          // Game variant - full interactivity with animations and particles
          return (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <button
                  id={slotId}
                  draggable={!isBeacon} // Cannot drag beacon
                  className={`relative transition-colors flex items-center justify-center overflow-visible ${
                    usable ? "hover:brightness-125 cursor-pointer" : "cursor-default"
                  }`}
                  style={{
                    width: slotSize,
                    height: slotSize,
                    borderRadius: 2 * scale,
                    backgroundColor: itemDef.image ? "rgb(31, 41, 55)" : itemDef.iconColor,
                    opacity: onCooldown ? 0.7 : 1,
                    border: isPending
                      ? "2px solid #00CED1"
                      : isBeacon
                        ? "1px solid #3b82f6" // Blue border for beacon
                        : `1px solid ${usable ? "#fbbf24" : "#6b7280"}`,
                    boxShadow: isPending
                      ? "0 0 8px rgba(0, 206, 209, 0.6)"
                      : isBeacon
                        ? "0 0 6px rgba(59, 130, 246, 0.4)"
                        : undefined,
                  }}
                  onClick={disableClicks ? undefined : () => {
                    if (usable && onUseItem) {
                      // Flash the white outline briefly
                      if (itemDef.image) {
                        setFlashingSlot(i)
                      }
                      onUseItem(i)
                    }
                  }}
                  onDragStart={(e) => {
                    if (isBeacon) {
                      e.preventDefault() // Prevent dragging beacon
                      return
                    }
                    e.dataTransfer.setData("application/json", JSON.stringify({ slotIndex: i, itemDefId: slot.itemDefId }))
                    e.dataTransfer.effectAllowed = "move"
                    onDragStart?.(i, slot.itemDefId)
                  }}
                  onDragEnd={() => {
                    if (!isBeacon) {
                      onDragEnd?.()
                    }
                  }}
                >
                  {/* Item icon - use sprite if available */}
                  <ItemSpriteIcon
                    itemDef={itemDef}
                    size={Math.floor(slotSize * 0.9)}
                    showOutline={isFlashing}
                  />

                  {/* Beacon charges badge */}
                  {isBeacon && (
                    <span
                      className="absolute bottom-0 right-0 bg-blue-600/90 text-white font-bold flex items-center justify-center z-10"
                      style={{
                        fontSize: 8 * scale,
                        width: 10 * scale,
                        height: 10 * scale,
                        borderRadius: 2 * scale,
                      }}
                    >
                      {slot.charges || 0}
                    </span>
                  )}

                  {/* Quantity badge (for non-beacon items) */}
                  {!isBeacon && slot.quantity > 1 && (
                    <span
                      className="absolute bottom-0 right-0 bg-black/80 text-white font-bold flex items-center justify-center z-10"
                      style={{
                        fontSize: 8 * scale,
                        width: 10 * scale,
                        height: 10 * scale,
                        borderRadius: 2 * scale,
                      }}
                    >
                      {slot.quantity}
                    </span>
                  )}

                  {/* Blue particle effects for beacon */}
                  {isBeacon && beaconParticles.map(p => (
                    <div
                      key={p.id}
                      className="absolute rounded-full pointer-events-none"
                      style={{
                        left: p.x,
                        top: p.y,
                        width: p.size * scale,
                        height: p.size * scale,
                        backgroundColor: `rgba(59, 130, 246, ${p.opacity})`,
                        boxShadow: `0 0 ${p.size * 2}px rgba(59, 130, 246, ${p.opacity * 0.7})`,
                        transform: "translate(-50%, -50%)",
                        zIndex: 30,
                      }}
                    />
                  ))}

                  {/* Cooldown overlay */}
                  {onCooldown && (
                    <div
                      className="absolute inset-0 bg-black/60 flex items-center justify-center z-20"
                      style={{ borderRadius: 2 * scale }}
                    >
                      <span className="text-white font-bold" style={{ fontSize: 8 * scale }}>
                        {Math.ceil(slot.currentCooldown / 60)}s
                      </span>
                    </div>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-black/95 border border-amber-600/50 shadow-lg shadow-black/50 p-0">
                <ItemTooltip itemDef={itemDef} slot={slot} variant="game" />
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </TooltipProvider>
  )
}
